import { buildResourceGroupTree } from '@modules/project-keys/mappers/buildResourceGroupTree'
import type { ProjectResourceGroup } from '@modules/project-keys/project-keys.interface'
import { resolveGatewayProjectKeyId } from '@modules/project-keys/utils/gatewayMatchesProjectKey'

import {
  GATEWAY_LIST_FILTER_ALL,
  GATEWAY_LIST_MODULE_OPTIONS,
  GATEWAY_LIST_PROJECT_KEY_OPTIONS,
  GATEWAY_LIST_RUNTIME_OPTIONS,
  GATEWAY_LIST_STATUS_OPTIONS,
  type GatewayListSelectOption,
} from '../constants/gatewayListFilters.const'
import type { GatewayListFilterFormSlice, GatewayListFilterTraitsSource } from '../types/gatewayListFilters.types'

import { filterSourcesForGatewayListOptions } from './filterSourcesForGatewayListOptions'
import { readGatewayListHostedModules, resolveGatewayListFilterTraits } from './resolveGatewayListFilterTraits'

function filterOptionsByUsedIds(
  options: readonly GatewayListSelectOption[],
  used: Set<string>
): GatewayListSelectOption[] {
  return options.filter((option) => option.id === GATEWAY_LIST_FILTER_ALL || used.has(option.id))
}

export function buildGatewayListStatusOptions(
  sources: GatewayListFilterTraitsSource[],
  filters?: GatewayListFilterFormSlice
): GatewayListSelectOption[] {
  const scoped = filters ? filterSourcesForGatewayListOptions(sources, filters, 'status') : sources
  const used = new Set<string>()
  for (const source of scoped) {
    const status = resolveGatewayListFilterTraits(source).status
    if (status) used.add(status)
  }
  return filterOptionsByUsedIds(GATEWAY_LIST_STATUS_OPTIONS, used)
}

export function buildGatewayListRuntimeOptions(
  sources: GatewayListFilterTraitsSource[],
  filters?: GatewayListFilterFormSlice
): GatewayListSelectOption[] {
  const scoped = filters ? filterSourcesForGatewayListOptions(sources, filters, 'runtime') : sources
  const used = new Set<string>()
  for (const source of scoped) {
    const runtime = resolveGatewayListFilterTraits(source).runtime
    if (runtime) used.add(runtime)
  }
  return filterOptionsByUsedIds(GATEWAY_LIST_RUNTIME_OPTIONS, used)
}

export function buildGatewayListModuleOptions(
  sources: GatewayListFilterTraitsSource[],
  filters?: GatewayListFilterFormSlice
): GatewayListSelectOption[] {
  const scoped = filters ? filterSourcesForGatewayListOptions(sources, filters, 'feed') : sources
  const names = new Set<string>()

  for (const source of scoped) {
    for (const hostedModule of readGatewayListHostedModules(source)) {
      const name = hostedModule.name?.trim()
      if (!name) {
        continue
      }
      names.add(name)
    }
  }

  const dynamicOptions = [...names]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((name) => ({ id: name, label: name }))

  const [allOption] = GATEWAY_LIST_MODULE_OPTIONS
  return [allOption, ...dynamicOptions]
}

export function buildGatewayListProjectKeyOptions(
  sources: GatewayListFilterTraitsSource[],
  groups: ProjectResourceGroup[],
  filters?: GatewayListFilterFormSlice
): GatewayListSelectOption[] {
  const [allOption] = GATEWAY_LIST_PROJECT_KEY_OPTIONS

  if (groups.length > 0) {
    const dynamicOptions = buildResourceGroupTree(groups).map((group) => ({
      id: group.id,
      label: `${'  '.repeat(group.depth)}${group.name}`,
    }))
    return [allOption, ...dynamicOptions]
  }

  const scoped = filters ? filterSourcesForGatewayListOptions(sources, filters, 'projectKey') : sources
  const usedIds = new Set<string>()

  for (const source of scoped) {
    const projectKeyId = resolveGatewayProjectKeyId(source)
    if (projectKeyId) {
      usedIds.add(projectKeyId)
    }
  }

  const orphanOptions = [...usedIds]
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    .map((id) => ({ id, label: id }))

  return [allOption, ...orphanOptions]
}
